import { useState, useEffect, useCallback, useRef } from 'react';
import type { PlayedCard } from '../../types/counting';

// ── Types ──────────────────────────────────────────────────────────────────

export type AnimationState = 'INITIAL' | 'PLAYING' | 'STOPPED' | 'COMPLETED';
export type AnimationSpeed = 3000 | 2000 | 1000;

export interface UseTrickAnimationProps {
  /** All cards played in the hand (all tricks, not just up to stop trick) */
  allPlayedCards: PlayedCard[];
  /** Number of the stop trick (1-based) — animation auto-stops here first */
  stopAfterTrick: number;
  /** Total number of tricks played in the hand (10-13) */
  totalTricksPlayed: number;
  /** If true, skip INITIAL state and start playing immediately (for E2E tests) */
  autoplay?: boolean;
}

export interface UseTrickAnimationReturn {
  /** Current animation state */
  animationState: AnimationState;
  /** Current card index (0-based into allPlayedCards). -1 means no cards shown yet. */
  currentCardIndex: number;
  /** Current animation speed in ms */
  speed: AnimationSpeed;
  /** Cards visible in the current trick being displayed (0-4 cards) */
  currentTrickCards: PlayedCard[];
  /** All cards played up to and including currentCardIndex (for hand removal) */
  cardsPlayedSoFar: PlayedCard[];
  /** Whether the stop trick has been reached at least once (for Submit button) */
  hasReachedStopTrick: boolean;
  /** Whether the last trick of the game has been reached at least once (for Claimed indicator) */
  hasReachedLastTrick: boolean;
  /** Actions */
  /** Start animation from INITIAL state (called by the initial Play button) */
  startInitial: () => void;
  play: () => void;
  stop: () => void;
  nextCard: () => void;
  prevCard: () => void;
  nextTrick: () => void;
  prevTrick: () => void;
  setSpeed: (speed: AnimationSpeed) => void;
  /** Reset animation (for new problem) */
  reset: () => void;
}

// ── Hook ───────────────────────────────────────────────────────────────────

export function useTrickAnimation({
  allPlayedCards,
  stopAfterTrick,
  totalTricksPlayed,
  autoplay = false,
}: UseTrickAnimationProps): UseTrickAnimationReturn {
  // FR-005..7: respect OS prefers-reduced-motion. Mount-time check only —
  // a mid-session toggle requires page reload to take effect. When the user
  // has requested reduced motion, suppress auto-play; user-pressed Play
  // (startInitial / play()) is unconditional.
  const prefersReducedMotionRef = useRef<boolean>(
    typeof window !== 'undefined' && typeof window.matchMedia === 'function'
      ? window.matchMedia('(prefers-reduced-motion: reduce)').matches
      : false
  );
  const shouldAutoplay = autoplay && !prefersReducedMotionRef.current;

  const [animationState, setAnimationState] = useState<AnimationState>(shouldAutoplay ? 'PLAYING' : 'INITIAL');
  const [currentCardIndex, setCurrentCardIndex] = useState(-1);
  const [speed, setSpeedState] = useState<AnimationSpeed>(2000);
  const [hasReachedStopTrick, setHasReachedStopTrick] = useState(false);
  const [hasReachedLastTrick, setHasReachedLastTrick] = useState(false);
  /** Whether the first phase (up to stop trick) has completed — used to know
   *  if Play should continue to the game's last trick or to the stop trick. */
  const [stopTrickReached, setStopTrickReached] = useState(false);

  // When autoplay is true, ensure animation starts (handles URL param changes on reload)
  useEffect(() => {
    if (shouldAutoplay) {
      setAnimationState('PLAYING');
      setResetCounter(c => c + 1);
    }
  }, [shouldAutoplay]);

  // Counter that increments on every reset — forces the auto-advance effect to re-run
  const [resetCounter, setResetCounter] = useState(0);

  // Ref to hold the interval/timeout so we can clear it without depending on state
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const pauseTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Keep refs to the latest values so callbacks always read the most recent
  // state without needing them as dependencies.
  const currentCardIndexRef = useRef(currentCardIndex);
  currentCardIndexRef.current = currentCardIndex;

  const animationStateRef = useRef(animationState);
  animationStateRef.current = animationState;

  // Ref for stopTrickReached so interval callback can read latest value
  const stopTrickReachedRef = useRef(stopTrickReached);
  stopTrickReachedRef.current = stopTrickReached;

  // Compute key indices
  // Last card index of the stop trick (0-based): trick N has cards at indices [(N-1)*4 .. N*4-1]
  const stopTrickLastCardIndex = stopAfterTrick * 4 - 1;
  // Last card index of the entire game
  const lastIndex = totalTricksPlayed * 4 - 1;

  // ── Helpers ────────────────────────────────────────────────────────────

  /** Clear any running interval or pause timeout */
  const clearAnimationInterval = useCallback(() => {
    if (intervalRef.current !== null) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    if (pauseTimeoutRef.current !== null) {
      clearTimeout(pauseTimeoutRef.current);
      pauseTimeoutRef.current = null;
    }
  }, []);

  // Forward-declare so startInterval and resumeAfterPause can reference each other
  const startIntervalRef = useRef<((ms: number) => void) | null>(null);

  /** Resume auto-advance after a between-trick pause */
  const resumeAfterPause = useCallback(
    (ms: number) => {
      pauseTimeoutRef.current = setTimeout(() => {
        pauseTimeoutRef.current = null;
        // Guard: only restart auto-advance if still PLAYING (stop() may have
        // been called while this timeout was pending in the macrotask queue)
        if (animationStateRef.current === 'PLAYING') {
          startIntervalRef.current?.(ms);
        }
      }, 1000); // 1-second pause between tricks
    },
    [],
  );

  /** Start (or restart) the auto-advance interval at the given speed */
  const startInterval = useCallback(
    (ms: number) => {
      clearAnimationInterval();
      intervalRef.current = setInterval(() => {
        const idx = currentCardIndexRef.current;
        const reachedStop = stopTrickReachedRef.current;

        // Determine the current target: stop trick if not yet reached, otherwise last trick
        const targetIndex = reachedStop ? lastIndex : stopTrickLastCardIndex;

        if (idx >= targetIndex) {
          // Reached the current target
          clearAnimationInterval();
          if (!reachedStop) {
            // First phase complete: stop at the stop trick
            setStopTrickReached(true);
            stopTrickReachedRef.current = true;
            setHasReachedStopTrick(true);
            // If stop trick IS the last trick, go to COMPLETED
            if (stopTrickLastCardIndex >= lastIndex) {
              setAnimationState('COMPLETED');
              setHasReachedLastTrick(true);
            } else {
              setAnimationState('STOPPED');
            }
          } else {
            // Second phase complete: reached last trick of game
            setAnimationState('COMPLETED');
            setHasReachedLastTrick(true);
          }
          return;
        }
        const nextIdx = idx + 1;
        setCurrentCardIndex(nextIdx);
        // Check if we just reached the target
        if (nextIdx >= targetIndex) {
          clearAnimationInterval();
          if (!reachedStop) {
            setStopTrickReached(true);
            stopTrickReachedRef.current = true;
            setHasReachedStopTrick(true);
            if (stopTrickLastCardIndex >= lastIndex) {
              setAnimationState('COMPLETED');
              setHasReachedLastTrick(true);
            } else {
              setAnimationState('STOPPED');
            }
          } else {
            setAnimationState('COMPLETED');
            setHasReachedLastTrick(true);
          }
        } else if ((nextIdx + 1) % 4 === 0 && nextIdx > 0) {
          // Last card of a trick — pause 1 second before next trick
          clearAnimationInterval();
          resumeAfterPause(ms);
        }
      }, ms);
    },
    [clearAnimationInterval, lastIndex, stopTrickLastCardIndex, resumeAfterPause],
  );

  // Keep the ref in sync so resumeAfterPause can call startInterval
  startIntervalRef.current = startInterval;

  // ── Auto-advance effect ────────────────────────────────────────────────
  // Start/stop the interval when animationState, speed, or resetCounter changes.
  useEffect(() => {
    if (animationState === 'PLAYING' && allPlayedCards.length > 0) {
      startInterval(speed);
    } else {
      clearAnimationInterval();
    }
    return clearAnimationInterval;
    // resetCounter forces this effect to re-run after a reset() call
  }, [animationState, speed, startInterval, clearAnimationInterval, allPlayedCards.length, resetCounter]);

  // ── Derived values ─────────────────────────────────────────────────────

  // Which trick number the currentCardIndex belongs to (0-based trick number)
  const currentTrickNumber = currentCardIndex >= 0 ? Math.floor(currentCardIndex / 4) : -1;

  // Cards visible in the current trick (only the portion up to currentCardIndex)
  const currentTrickCards: PlayedCard[] = (() => {
    if (currentCardIndex < 0) return [];
    const trickStartIndex = currentTrickNumber * 4;
    // Only show cards up to the number actually played (for partial last trick in claimed hands)
    const endIndex = Math.min(currentCardIndex + 1, allPlayedCards.length);
    return allPlayedCards.slice(trickStartIndex, endIndex);
  })();

  // All cards played so far (for hand removal display)
  const cardsPlayedSoFar: PlayedCard[] =
    currentCardIndex >= 0 ? allPlayedCards.slice(0, Math.min(currentCardIndex + 1, allPlayedCards.length)) : [];

  // ── Actions ────────────────────────────────────────────────────────────

  /** Start animation from INITIAL state (called by the initial Play button) */
  const startInitial = useCallback(() => {
    if (animationStateRef.current !== 'INITIAL') return;
    setAnimationState('PLAYING');
    setResetCounter(c => c + 1);
  }, []);

  const play = useCallback(() => {
    if (animationState === 'COMPLETED') {
      // Restart from the beginning (replay)
      setCurrentCardIndex(-1);
      currentCardIndexRef.current = -1;
      setHasReachedStopTrick(false);
      setHasReachedLastTrick(false);
      setStopTrickReached(false);
      stopTrickReachedRef.current = false;
      setAnimationState('PLAYING');
      setResetCounter(c => c + 1);
      return;
    }
    // Resume animation (from STOPPED state) — continues toward next target
    setAnimationState('PLAYING');
  }, [animationState]);

  const stop = useCallback(() => {
    clearAnimationInterval();
    setAnimationState('STOPPED');
  }, [clearAnimationInterval]);

  const nextCard = useCallback(() => {
    if (animationState !== 'STOPPED') return;
    setCurrentCardIndex((prev) => {
      const next = prev + 1;
      if (next > lastIndex) return prev;
      // Check if we reached the stop trick
      if (next >= stopTrickLastCardIndex && !hasReachedStopTrick) {
        setStopTrickReached(true);
        stopTrickReachedRef.current = true;
        setHasReachedStopTrick(true);
      }
      // Check if we reached the last trick
      if (next >= lastIndex) {
        setHasReachedLastTrick(true);
        setAnimationState('COMPLETED');
      }
      return next;
    });
  }, [animationState, lastIndex, stopTrickLastCardIndex, hasReachedStopTrick]);

  const prevCard = useCallback(() => {
    if (animationState === 'PLAYING' || animationState === 'INITIAL') return;
    if (animationState === 'COMPLETED') setAnimationState('STOPPED');
    setCurrentCardIndex((prev) => Math.max(-1, prev - 1));
  }, [animationState]);

  const nextTrick = useCallback(() => {
    if (animationState !== 'STOPPED') return;
    setCurrentCardIndex((prev) => {
      // Current trick (0-based)
      const currentTrick = prev >= 0 ? Math.floor(prev / 4) : -1;
      // Last card of next trick
      const nextTrickLastCard = (currentTrick + 1) * 4 + 3;
      const clamped = Math.min(nextTrickLastCard, lastIndex);
      // Check if we reached the stop trick
      if (clamped >= stopTrickLastCardIndex && !hasReachedStopTrick) {
        setStopTrickReached(true);
        stopTrickReachedRef.current = true;
        setHasReachedStopTrick(true);
      }
      // Check if we reached the last trick
      if (clamped >= lastIndex) {
        setHasReachedLastTrick(true);
        setAnimationState('COMPLETED');
      }
      return clamped;
    });
  }, [animationState, lastIndex, stopTrickLastCardIndex, hasReachedStopTrick]);

  const prevTrick = useCallback(() => {
    if (animationState === 'PLAYING' || animationState === 'INITIAL') return;
    if (animationState === 'COMPLETED') setAnimationState('STOPPED');
    setCurrentCardIndex((prev) => {
      if (prev < 0) return -1;
      // Current trick (0-based)
      const currentTrick = Math.floor(prev / 4);
      if (currentTrick === 0) {
        // Already in the first trick — go to "no cards shown" state
        return -1;
      }
      // Last card of previous trick (all 4 cards visible)
      return (currentTrick - 1) * 4 + 3;
    });
  }, [animationState]);

  const setSpeed = useCallback((newSpeed: AnimationSpeed) => {
    setSpeedState(newSpeed);
    // The useEffect watching `speed` will restart the interval automatically
  }, []);

  const reset = useCallback(() => {
    clearAnimationInterval();
    setCurrentCardIndex(-1);
    currentCardIndexRef.current = -1;
    setHasReachedStopTrick(false);
    setHasReachedLastTrick(false);
    setStopTrickReached(false);
    stopTrickReachedRef.current = false;
    setAnimationState(shouldAutoplay ? 'PLAYING' : 'INITIAL');
    setResetCounter(c => c + 1);
  }, [clearAnimationInterval, shouldAutoplay]);

  // ── Return ─────────────────────────────────────────────────────────────

  return {
    animationState,
    currentCardIndex,
    speed,
    currentTrickCards,
    cardsPlayedSoFar,
    hasReachedStopTrick,
    hasReachedLastTrick,
    startInitial,
    play,
    stop,
    nextCard,
    prevCard,
    nextTrick,
    prevTrick,
    setSpeed,
    reset,
  };
}
